import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { fontSize, fontWeight, spacing, opacity } from "@atlas-int/tokens";

export const ClearButtonSymbol = "\u2715";

type CloseButtonVariant = "inline" | "overlay";
type CloseButtonSize = "sm" | "md";

export interface CloseButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: CloseButtonVariant;
  size?: CloseButtonSize;
  label?: string;
}

const sizeStyles = {
  sm: css`
    font-size: ${fontSize.xs};
  `,
  md: css`
    font-size: ${fontSize["3xl"]};
    font-weight: ${fontWeight.light};
    padding: ${spacing[6]};
  `,
};

const overlayStyles = css`
  position: absolute;
  top: ${spacing[2]};
  right: ${spacing[2]};
  padding: 0;
`;

const StyledButton = styled.button<{
  $variant: CloseButtonVariant;
  $size: CloseButtonSize;
}>`
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${({ $size }) => sizeStyles[$size]}
  ${({ $variant }) => ($variant === "overlay" ? overlayStyles : "")}

  &:hover {
    opacity: ${opacity.hover};
  }
`;

const CloseButton = React.forwardRef<HTMLButtonElement, CloseButtonProps>(
  (
    {
      variant = "inline",
      size = "md",
      label = "Close",
      children,
      ...rest
    },
    ref,
  ) => {
    return (
      <StyledButton
        ref={ref}
        $variant={variant}
        $size={size}
        aria-label={label}
        {...rest}
      >
        {children ?? ClearButtonSymbol}
      </StyledButton>
    );
  },
);

CloseButton.displayName = "CloseButton";

export default CloseButton;
